import { Box } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { tokens } from '../theme';

type Tone = 'success' | 'warning' | 'error' | 'info' | 'neutral';

const TONES: Record<Tone, { bg: string; fg: string }> = {
  success: { bg: tokens.successSoft, fg: tokens.success },
  warning: { bg: tokens.warnSoft, fg: tokens.warn },
  error: { bg: tokens.errorSoft, fg: tokens.error },
  info: { bg: tokens.tint, fg: tokens.blueDeep },
  neutral: { bg: tokens.strip, fg: '#344054' },
};

const APP_TONES: Record<string, Tone> = {
  live: 'success',
  beta: 'info',
  planned: 'neutral',
  maintenance: 'warning',
};

const APP_ICONS: Record<string, SvgIconComponent> = {
  live: CheckCircleOutlineIcon,
  beta: InfoOutlinedIcon,
  planned: ScheduleOutlinedIcon,
  maintenance: BuildOutlinedIcon,
};

const SVC_TONES: Record<string, Tone> = {
  operational: 'success',
  degraded: 'warning',
  outage: 'error',
  maintenance: 'info',
};

const SVC_ICONS: Record<string, SvgIconComponent> = {
  operational: CheckCircleOutlineIcon,
  degraded: WarningAmberOutlinedIcon,
  outage: ErrorOutlineIcon,
  maintenance: BuildOutlinedIcon,
};

const REQ_TONES: Record<string, Tone> = {
  submitted: 'neutral',
  reviewing: 'info',
  scheduled: 'info',
  in_progress: 'warning',
  delivered: 'success',
  declined: 'error',
};

const REQ_ICONS: Record<string, SvgIconComponent> = {
  submitted: ScheduleOutlinedIcon,
  reviewing: InfoOutlinedIcon,
  scheduled: ScheduleOutlinedIcon,
  in_progress: HourglassEmptyOutlinedIcon,
  delivered: TaskAltOutlinedIcon,
  declined: CancelOutlinedIcon,
};

function Pill({ tone, label, Icon }: { tone: Tone; label: string; Icon: SvgIconComponent }) {
  const t = TONES[tone];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.25,
        py: 0.25,
        borderRadius: 999,
        bgcolor: t.bg,
        color: t.fg,
        fontSize: 13,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        textTransform: 'capitalize',
        lineHeight: 1.6,
      }}
    >
      <Icon aria-hidden sx={{ fontSize: 14 }} />
      {label}
    </Box>
  );
}

export function AppStatusChip({ status }: { status: string }) {
  return (
    <Pill
      tone={APP_TONES[status] ?? 'neutral'}
      label={status.replace('_', ' ')}
      Icon={APP_ICONS[status] ?? InfoOutlinedIcon}
    />
  );
}

export function ServiceStatusChip({ status }: { status: string }) {
  return (
    <Pill tone={SVC_TONES[status] ?? 'neutral'} label={status} Icon={SVC_ICONS[status] ?? InfoOutlinedIcon} />
  );
}

export function RequestStatusChip({ status }: { status: string }) {
  return (
    <Pill
      tone={REQ_TONES[status] ?? 'neutral'}
      label={status.replace('_', ' ')}
      Icon={REQ_ICONS[status] ?? InfoOutlinedIcon}
    />
  );
}
